import { httpClient } from '../utils/httpClient';
import {
  MatriculaPayload, MatriculaResponse,
  AsignacionPayload, AsignacionItem,
  CursosEstudianteResponse,
  AjustarTarifasPayload,
  MatriculaMasivaPayload, MatriculaMasivaResponse,
  MatriculaEstadisticas,
} from '../types/matricula.types';

// ============================================================
// matriculaService.ts
// data viene como array → tomamos el primer elemento
// ============================================================

export const matriculaService = {

  // GET → lista paginada/completa de matrículas con filtros opcionales
  async listar(params: {
    anioEscolarId?: number;
    gradoId?:       number;
    seccionId?:     number;
  } = {}): Promise<MatriculaResponse[]> {
    const q = new URLSearchParams();
    if (params.anioEscolarId) q.set('anioEscolarId', String(params.anioEscolarId));
    if (params.gradoId)       q.set('gradoId',       String(params.gradoId));
    if (params.seccionId)     q.set('seccionId',     String(params.seccionId));
    const qs = q.toString();
    return httpClient.get<MatriculaResponse[]>(`/matriculas${qs ? `?${qs}` : ''}`);
  },

  // GET /matriculas/anio-escolar/{anioEscolarId}
  async listarPorAnio(anioEscolarId: number, signal?: AbortSignal): Promise<MatriculaResponse[]> {
    return httpClient.get<MatriculaResponse[]>(`/matriculas/anio-escolar/${anioEscolarId}`, signal ? { signal } : undefined);
  },

  // GET /matriculas/estadisticas?anioEscolarId={id}
  async getEstadisticas(anioEscolarId: number, signal?: AbortSignal): Promise<MatriculaEstadisticas> {
    return httpClient.get<MatriculaEstadisticas>(
      `/matriculas/estadisticas?anioEscolarId=${anioEscolarId}`,
      signal ? { signal } : undefined,
    );
  },

  // GET /matriculas/anio-escolar/{anioEscolarId}/nivel/{nivel}
  async listarPorAnioNivel(anioEscolarId: number, nivel: string): Promise<MatriculaResponse[]> {
    return httpClient.get<MatriculaResponse[]>(`/matriculas/anio-escolar/${anioEscolarId}/nivel/${nivel}`);
  },

  // GET /matriculas/anio-escolar/{anioEscolarId}/nivel/{nivel}/grado/{gradoId}
  async listarPorAnioNivelGrado(anioEscolarId: number, nivel: string, gradoId: number): Promise<MatriculaResponse[]> {
    return httpClient.get<MatriculaResponse[]>(`/matriculas/anio-escolar/${anioEscolarId}/nivel/${nivel}/grado/${gradoId}`);
  },

  // GET /matriculas/anio-escolar/{anioEscolarId}/nivel/{nivel}/grado/{gradoId}/seccion/{seccionId}
  async listarFiltrado(anioEscolarId: number, nivel: string, gradoId: number, seccionId: number): Promise<MatriculaResponse[]> {
    return httpClient.get<MatriculaResponse[]>(`/matriculas/anio-escolar/${anioEscolarId}/nivel/${nivel}/grado/${gradoId}/seccion/${seccionId}`);
  },

  // GET /matriculas/buscar?dni=&anioEscolarId=  → matrícula activa del estudiante
  // 404 si no existe → devuelve null (no lanza error)
  async buscarPorDni(dni: string, anioEscolarId?: number): Promise<MatriculaResponse | null> {
    try {
      const q = new URLSearchParams({ dni });
      if (anioEscolarId) q.set('anioEscolarId', String(anioEscolarId));
      const data = await httpClient.get<MatriculaResponse[]>(`/matriculas/buscar?${q}`);
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    } catch {
      return null;
    }
  },

  // GET → data: MatriculaResponse[]  (tomamos el [0] si existe)
  async getByEstudiante(estudianteId: number): Promise<MatriculaResponse | null> {
    try {
      const data = await httpClient.get<MatriculaResponse[]>(
        `/matriculas/estudiante/${estudianteId}`
      );
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    } catch {
      return null;
    }
  },

  // GET /matriculas/inactivas?anioEscolarId={id}
  async listarInactivas(anioEscolarId: number): Promise<MatriculaResponse[]> {
    return httpClient.get<MatriculaResponse[]>(`/matriculas/inactivas?anioEscolarId=${anioEscolarId}`);
  },

  // GET → todas las matrículas de un estudiante (sin tomar solo la primera)
  async listarPorEstudiante(estudianteId: number): Promise<MatriculaResponse[]> {
    try {
      const data = await httpClient.get<MatriculaResponse[]>(
        `/matriculas/estudiante/${estudianteId}`
      );
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  },

  // POST → { estudianteId, gradoId, seccionId, anioEscolarId }
  async crear(payload: MatriculaPayload): Promise<MatriculaResponse> {
    return httpClient.post<MatriculaResponse>('/matriculas', payload);
  },

  // PUT → mismos campos del payload
  async modificar(idMatricula: number, payload: MatriculaPayload): Promise<MatriculaResponse> {
    return httpClient.put<MatriculaResponse>(`/matriculas/${idMatricula}`, payload);
  },

  // PUT → ajuste manual de tarifas (null = no modificar)
  async ajustarTarifas(idMatricula: number, payload: AjustarTarifasPayload): Promise<MatriculaResponse> {
    return httpClient.put<MatriculaResponse>(`/matriculas/${idMatricula}/ajustar-tarifas`, payload);
  },

  // DELETE
  async eliminar(idMatricula: number): Promise<void> {
    return httpClient.delete<void>(`/matriculas/${idMatricula}`);
  },

  // POST /matriculas/masivo
  async registrarMasivo(payload: MatriculaMasivaPayload): Promise<MatriculaMasivaResponse> {
    return httpClient.post<MatriculaMasivaResponse>('/matriculas/masivo', payload);
  },

  // GET → cursos del estudiante para un año escolar
  async getCursosByEstudiante(estudianteId: number, anioEscolarId: number): Promise<CursosEstudianteResponse> {
    return httpClient.get<CursosEstudianteResponse>(
      `/matriculas/estudiante/${estudianteId}/cursos?anioEscolarId=${anioEscolarId}`
    );
  },
};

// ============================================================
// asignacionService.ts
// data viene como array
// ============================================================

export const asignacionService = {

  // GET → data: AsignacionItem[]
  async getByDocente(docenteId: number): Promise<AsignacionItem[]> {
    try {
      const data = await httpClient.get<AsignacionItem[]>(
        `/asignaciones/docente/${docenteId}`
      );
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  },

  // POST → { docenteId, cursoId, gradoId, seccionId, anioEscolarId }
  async crear(payload: AsignacionPayload): Promise<AsignacionItem> {
    return httpClient.post<AsignacionItem>('/asignaciones', payload);
  },

  // PUT /asignaciones/{id} — envía el payload completo + el nuevo orden
  async actualizarOrden(asignacion: AsignacionItem, orden: number): Promise<AsignacionItem> {
    return httpClient.put<AsignacionItem>(`/asignaciones/${asignacion.idAsignacion}`, {
      docenteId:     asignacion.docenteId,
      cursoId:       asignacion.cursoId,
      gradoId:       asignacion.gradoId,
      seccionId:     asignacion.seccionId,
      anioEscolarId: asignacion.anioEscolarId,
      orden,
    });
  },

  // DELETE
  async eliminar(idAsignacion: number): Promise<void> {
    return httpClient.delete<void>(`/asignaciones/${idAsignacion}`);
  },

  // Crea N asignaciones en paralelo (una por cursoId)
  async guardarLote(
    docenteId:     number,
    cursosIds:     number[],
    gradoId:       number,
    seccionId:     number,
    anioEscolarId: number,
  ): Promise<AsignacionItem[]> {
    return Promise.all(
      cursosIds.map(cursoId =>
        asignacionService.crear({ docenteId, cursoId, gradoId, seccionId, anioEscolarId })
      )
    );
  },
};